<!--
  @component RollbackIO — P3 effecting lens (r-arc, the capstone).
  chain:   ctx.deployment.dispatch("rollback.*", …) → this → ctx.rollback
  effects: restoreToPoint (the W3-ordered atomic restore) +
           runSupplyChain (build → scan → GATE → sign → push → pin).
           Unwired ⇒ NotWiredError (FACE-LEDGER).
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { ROLLBACK_IO_KEY } from "./deployment-io.js";
  import type { RollbackIOContext, RestorePointRef } from "./deployment-io.js";
  export { ROLLBACK_IO_KEY };
  export type { RollbackIOContext, RestorePointRef };
  export function useRollbackIO(): RollbackIOContext {
    const ctx = getContext<RollbackIOContext | undefined>(ROLLBACK_IO_KEY);
    if (!ctx) throw new Error("useRollbackIO() called outside of a RollbackIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: RollbackIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("rollback.")); },
    restoreToPoint: (point, options?) => dispatchOp("rollback.restoreToPoint", { point, options }),
    runSupplyChain: (artifactRef, options?) => dispatchOp("rollback.runSupplyChain", { artifactRef, options }),
  } satisfies RollbackIOContext;

  setContext(ROLLBACK_IO_KEY, ctx);
</script>
{@render children?.()}
